import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  setSavingGoalAmount,
  setExistingInvestment,
  setExpectedInflation,
  setTimePeriod,
  setMonthlyInvestment,
  setFutureValueOfGoal,
  setExpectedReturn,
  setFutureValueOfExistingInvestment,
  resetSavingData,
  setSavingErrors,
} from "../redux/calculations/savingSlice";
import {
  resetChartData,
  updateSavingsCalculation,
} from "../redux/calculations/calculaterSlice";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const SavingCalc = () => {
  const [showTable, setShowTable] = useState(false);
  const { textData } = useLocale();
  const { savingCalc } = textData;
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const { t } = useTranslation();
  const [calcDone, setCalcDone] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);

  const dispatch = useDispatch();
  const {
    savingGoalAmount,
    existingInvestment,
    expectedInflation,
    timePeriod,
    expectedReturn,
    savingErrors,
  } = useSelector((state) => state.savingsCalculator);

  const [formattedLoanAmount, setFormattedLoanAmount] = useState("");

  const formik = useFormik({
    initialValues: {
      savingGoalAmount: savingGoalAmount,
      existingInvestment: existingInvestment,
      expectedInflation: expectedInflation,
      timePeriod: timePeriod,
      expectedReturn: expectedReturn,
    },
    validationSchema: Yup.object({
      savingGoalAmount: Yup.number()
        .min(10000, savingCalc.savingGoalAmount)
        .max(5000000, savingCalc.savingGoalAmount)

        .required(savingCalc.savingrequired),
      existingInvestment: Yup.number()
        .min(0, savingCalc.existingInvestment)
        .max(5000000, savingCalc.existingInvestment)
        .required(savingCalc.existingInvestmentRequired),
      expectedInflation: Yup.number()
        .min(2, savingCalc.expectedInflation)
        .max(9, savingCalc.expectedInflation)
        .required(savingCalc.expectedInflationRequired),
      expectedReturn: Yup.number()
        .min(1, savingCalc.expectedReturn)
        .max(20, savingCalc.expectedReturn)
        .required(savingCalc.expectedReturnRequired),
      timePeriod: Yup.number()
        .min(0, savingCalc.timePeriod)
        .max(35, savingCalc.timePeriod)
        .required(savingCalc.timePeriodRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_SAVING");
      }
      const {
        savingGoalAmount,
        existingInvestment,
        expectedInflation,
        expectedReturn,
        timePeriod,
      } = values;

      if (existingInvestment >= savingGoalAmount) {
        dispatch(setSavingErrors(textData.savingCalc.exceedError));
        setTimeout(() => {
          dispatch(setSavingErrors(null));
        }, 3000);
        return;
      }

      const futureValueOfGoal =
        savingGoalAmount * Math.pow(1 + expectedInflation / 100, timePeriod);

      const futureValueOfExistingInvestment =
        existingInvestment * Math.pow(1 + expectedReturn / 100, timePeriod);

      const requiredFutureValue =
        futureValueOfGoal - futureValueOfExistingInvestment;

      const monthlyRate = expectedReturn / 100 / 12;
      const totalMonths = timePeriod * 12;

      const monthlyInvestment =
        (requiredFutureValue * monthlyRate) /
        (Math.pow(1 + monthlyRate, totalMonths) - 1);

      dispatch(setFutureValueOfGoal(futureValueOfGoal));
      dispatch(
        setFutureValueOfExistingInvestment(futureValueOfExistingInvestment)
      );
      dispatch(setMonthlyInvestment(monthlyInvestment));

      dispatch(
        updateSavingsCalculation({
          monthlyInvestment: monthlyInvestment,
          // futureValueOfGoal: Math.round(futureValueOfGoal),
          futureValueOfExistingInvestment: Math.round(
            futureValueOfExistingInvestment
          ),
        })
      );

      saveToHistory({
        tabName: "savingCalc",
        totalInvestment: savingGoalAmount,
        existingInvestment: existingInvestment,
        expectedInflation: expectedInflation,
        expectedReturn: expectedReturn,
        timePeriod: timePeriod,
        monthlyInvestment: monthlyInvestment,
        futureValueOfGoal: futureValueOfGoal,
        futureValueOfExistingInvestment: futureValueOfExistingInvestment,
        calculationType: "SavingGoal",
      });

      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetSavingData());
    };
  }, [dispatch]);
  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        tabName: calculationData.tabName,
        savingGoalAmount: calculationData.totalInvestment,
        existingInvestment: calculationData.existingInvestment,
        expectedInflation: calculationData.expectedInflation,
        expectedReturn: calculationData.expectedReturn,
        timePeriod: calculationData.timePeriod,
        futureValueOfGoal: calculationData.futureValueOfGoal,
        futureValueOfExistingInvestment:
          calculationData.futureValueOfExistingInvestment,
        monthlyInvestment: calculationData.monthlyInvestment,
        calculationType: "SavingGoal",
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.savingGoalAmount === newHistoryRecord.savingGoalAmount &&
          record.existingInvestment === newHistoryRecord.existingInvestment &&
          record.expectedInflation === newHistoryRecord.expectedInflation &&
          record.expectedReturn === newHistoryRecord.expectedReturn &&
          record.timePeriod === newHistoryRecord.timePeriod
        );
      });

      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) { }
  };

  useEffect(() => {
    setFormattedLoanAmount(formatToINR(formik.values.loanAmount));
  }, [formik.values.loanAmount]);

  useEffect(() => {
    dispatch(setSavingGoalAmount(formik.values.savingGoalAmount));
    dispatch(setExistingInvestment(formik.values.existingInvestment));
    dispatch(setExpectedInflation(formik.values.expectedInflation));
    dispatch(setTimePeriod(formik.values.timePeriod));
    dispatch(setExpectedReturn(formik.values.expectedReturn));
  }, [formik.values, dispatch]);

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  const savingGoalSliderRef = useRef(null);
  const existingInvestmentSliderRef = useRef(null);
  const inflationSliderRef = useRef(null);
  const timePeriodSliderRef = useRef(null);
  const expectedReturnSliderRef = useRef(null);

  const handleSavingGoalSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setSavingGoalAmount(value));
    formik.setFieldValue("savingGoalAmount", value);
    setIsFormModified(true);
    setCalcDone(false);
  };
  const handleReturnSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setExpectedReturn(value));
    formik.setFieldValue("expectedReturn", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleExistingInvestmentSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setExistingInvestment(value));
    formik.setFieldValue("existingInvestment", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInflationSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setExpectedInflation(value));
    formik.setFieldValue("expectedInflation", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleTimePeriodSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTimePeriod(value));
    formik.setFieldValue("timePeriod", value);
    setIsFormModified(true);
    setCalcDone(false);
  };
  const handleExpectedInflationChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("expectedInflation", rawValue);
      dispatch(setExpectedInflation(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("expectedInflation", normalizedValue);
    dispatch(setExpectedInflation(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleReturnRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("expectedReturn", rawValue);
      dispatch(setExpectedReturn(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("expectedReturn", normalizedValue);
    dispatch(setExpectedReturn(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleSavingGoalChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("savingGoalAmount", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 9) return;

    if (rawValue === "0") {
      formik.setFieldValue("savingGoalAmount", 0);
      return;
    }
    formik.setFieldValue("savingGoalAmount", rawValue ? Number(rawValue) : "");
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInvestmentChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("existingInvestment", "");
      return;
    }

    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 9) return;

    if (rawValue === "0") {
      formik.setFieldValue("existingInvestment", 0);
      return;
    }
    formik.setFieldValue(
      "existingInvestment",
      rawValue ? Number(rawValue) : ""
    );
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.length > 2) return;
    formik.setFieldValue("timePeriod", rawValue);
    if (rawValue === "") return;
    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      const clamped = Math.max(1, Math.min(30, numericValue));
      dispatch(setTimePeriod(clamped));
    }
    setIsFormModified(true);
    setCalcDone(false);
  };
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.savingContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.savingContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.savingContent.howItWorksTitle}
      </h2>
      <p
        className="leading-relaxed mt-2 text-sm sm:text-[15px]"
        dangerouslySetInnerHTML={{
          __html: textData.savingContent.howItWorksDescription,
        }}
      />

      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        FV = PV × (1 + r)^n{" "}
      </p>

      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.savingContent.exampleDescription}
      </p>

      <table className="border-collapse mt-4 w-full">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-3 border border-gray-light w-1/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.savingContent.symbolColumn}
            </th>
            <th className="p-3 border border-gray-light w-2/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.savingContent.descriptionColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.savingContent.sgcSymbolsTable[0].symbol}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.savingContent.sgcSymbolsTable[0].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.savingContent.sgcSymbolsTable[1].symbol}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.savingContent.sgcSymbolsTable[1].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.savingContent.sgcSymbolsTable[2].symbol}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.savingContent.sgcSymbolsTable[2].description}
            </td>
          </tr>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              {textData.savingContent.sgcSymbolsTable[3].symbol}
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.savingContent.sgcSymbolsTable[3].description}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.savingContent.monthlyInvestmentNote}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.savingContent.howToUseTitle}
      </h2>
      <ol className="list-decimal px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li> {textData.savingContent.howToUseSteps[0]}</li>
        <li> {textData.savingContent.howToUseSteps[1]}</li>
        <li> {textData.savingContent.howToUseSteps[2]}</li>
        <li> {textData.savingContent.howToUseSteps[3]}</li>
        <li> {textData.savingContent.howToUseSteps[4]}</li>
        <li> {textData.savingContent.howToUseSteps[5]}</li>
        <li> {textData.savingContent.howToUseSteps[6]}</li>
      </ol>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.savingContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.savingContent.benefits[0]}</li>
        <li>{textData.savingContent.benefits[1]}</li>
        <li>{textData.savingContent.benefits[2]}</li>
        <li>{textData.savingContent.benefits[3]}</li>
        <li>{textData.savingContent.benefits[4]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.savingContent.tipTitle}:
      </h2>
      <p className="font-medium  text-sm sm:text-base p-3 mt-3">
        {textData.savingContent.tipDescription}{" "}
      </p>
    </div>
  );
  const getFormattedPlaceholder = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  const timeLabel = t("yearShort");

  return (
    <>
      <Helmet>
        <title>GrowLifeFund Savings Goal Calculator | Power Your Savings</title>
        <meta
          name="description"
          content="GrowLifeFund Savings Goal Calculator lets you set smarter targets, track progress easily, and supercharge your savings to hit your financial goals faster."
        />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/saving-goal-calculator"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="GrowLifeFund Savings Goal Calculator | Power Your Savings"
        />
        <meta
          property="og:description"
          content="GrowLifeFund Savings Goal Calculator lets you set smarter targets, track progress easily, and supercharge your savings to hit your financial goals faster."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/savings-goal-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Savings Goal Calculator by GrowLifeFund"
        />

        {/* Robots */}
        <meta name="robots" content="index, follow" />
      </Helmet>
      <CalculatorLayout
        title={
          <>
            {textData.savingCalc.title}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.savingCalc.Amount}
              {""}
            </span>
          </>
        }
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
        description={textData.savingCalc.description}
        descriptionContent={descriptionContent}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.savingCalc.savingGoalAmountLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={savingGoalSliderRef}>

                  <input
                    type="range"
                    min={10000}
                    max={5000000}
                    step={1000}
                    value={formik.values.savingGoalAmount}
                    onChange={handleSavingGoalSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((savingGoalAmount - 10000) / (5000000 - 10000)) * 100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="savingGoalAmount"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.savingGoalAmount)}
                    onChange={handleSavingGoalChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(10000, i18n.language)}
                    borderColor={
                      formik.touched.savingGoalAmount &&
                        formik.errors.savingGoalAmount
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]     border ${formik.touched.savingGoalAmount &&
                        formik.errors.savingGoalAmount
                        ? "border-danger"
                        : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.savingGoalAmount &&
                    formik.errors.savingGoalAmount && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.savingGoalAmount}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.savingCalc.existingInvestmentLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={existingInvestmentSliderRef}
                >

                  <input
                    type="range"
                    min={0}
                    max={5000000}
                    step={1000}
                    value={formik.values.existingInvestment}
                    onChange={handleExistingInvestmentSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((existingInvestment - 0) / (5000000 - 0)) * 100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="existingInvestment"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.existingInvestment)}
                    onChange={handleInvestmentChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(1000, i18n.language)}
                    borderColor={
                      formik.touched.existingInvestment &&
                        formik.errors.existingInvestment
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]    border ${formik.touched.existingInvestment &&
                        formik.errors.existingInvestment
                        ? "border-danger"
                        : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.existingInvestment &&
                    formik.errors.existingInvestment && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.existingInvestment}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* expected return */}
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.savingCalc.expectedReturnLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={expectedReturnSliderRef}
                >

                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={0.1}
                    value={parseFloat(formik.values.expectedReturn)}
                    className="flex-1 min-w-0 max-w-[314px]"
                    onChange={handleReturnSliderChange}
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((expectedReturn - 1) / (20 - 1)) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="expectedReturn"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("8.0")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.expectedReturn
                    )}
                    onChange={handleReturnRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.expectedReturn &&
                        formik.errors.expectedReturn
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]     border ${formik.touched.expectedReturn &&
                        formik.errors.expectedReturn
                        ? "border-danger"
                        : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.expectedReturn &&
                    formik.errors.expectedReturn && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.expectedReturn}
                      </div>
                    )}
                </div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.savingCalc.expectedInflationLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={inflationSliderRef}>

                  <input
                    type="range"
                    min={2}
                    max={9}
                    step={0.1}
                    value={parseFloat(formik.values.expectedInflation)}
                    onChange={handleInflationSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((expectedInflation - 2) / (9 - 2)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="expectedInflation"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("8.0")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.expectedInflation
                    )}
                    onChange={handleExpectedInflationChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.expectedInflation &&
                        formik.errors.expectedInflation
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]     border ${formik.touched.expectedInflation &&
                        formik.errors.expectedInflation
                        ? "border-danger"
                        : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.expectedInflation &&
                    formik.errors.expectedInflation && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.expectedInflation}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.savingCalc.timePeriodLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={timePeriodSliderRef}>

                  <input
                    type="range"
                    min={0}
                    max={35}
                    step={1}
                    value={formik.values.timePeriod}
                    onChange={handleTimePeriodSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((timePeriod - 0) / (35 - 0)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="timePeriod"
                    leftIcon={timeLabel}
                    value={formik.values.timePeriod}
                    onChange={handleTimeChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"2"}
                    borderColor={
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]     border ${formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-danger"
                        : "border-gray-light"
                      }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.timePeriod && formik.errors.timePeriod && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.timePeriod}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px] relative">
              <Button type="submit" className="w-full mb-2">
                {textData.calculate}
              </Button>
              {savingErrors && (
                <div className=" absolute bottom-[-50] z-50 w-full text-red-500 mb-4 text-center px-2 py-2 sm:py-4  rounded-md text-black bg-gray-light border-none text-sm">
                  {savingErrors}
                </div>
              )}
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default SavingCalc;
